// =============================================================================
// BLISS FUNNEL ANALYTICS
// One seam for every funnel event so drop-off is measurable.
//
// Two destinations, both optional and both fail-safe:
//   1. Session breadcrumbs (always) — attached to the lead record on submit,
//      so sales can see exactly how far someone got and in what order.
//   2. GA4 / Meta Pixel / GTM dataLayer (only when configured) — the only way
//      to see visitors who never hand over contact info, e.g. gate abandons.
//
// Configure with VITE_GA4_MEASUREMENT_ID and/or VITE_META_PIXEL_ID. With
// neither set every call is a local no-op, so the funnel keeps working the
// same way it does today.
// =============================================================================

import { rememberMarketingTouch } from './marketingAttributionService'

export type FunnelEventParams = Record<string, string | number | boolean>

const BREADCRUMB_KEY = 'bliss_funnel_events'
const MAX_BREADCRUMBS = 40

type AnalyticsWindow = Window & {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
  fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean; version?: string; callMethod?: (...args: unknown[]) => void; push?: unknown }
  _fbq?: unknown
}

let initialized = false
let ga4Id: string | null = null
let metaPixelId: string | null = null

/** GA4 accepts letters, digits and underscores, must start with a letter, max 40 chars. */
export function normalizeFunnelEventName(event: string): string {
  const cleaned = event
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
  const named = /^[a-z]/.test(cleaned) ? cleaned : `funnel_${cleaned}`
  return named.slice(0, 40).replace(/_+$/, '')
}

function readEnvId(key: string): string | null {
  try {
    const value = (import.meta.env as Record<string, string | undefined>)?.[key]
    return value && value.trim() ? value.trim() : null
  } catch {
    return null
  }
}

function injectScript(src: string): void {
  if (document.querySelector(`script[src="${src}"]`)) return
  const script = document.createElement('script')
  script.async = true
  script.src = src
  document.head.appendChild(script)
}

function startGa4(id: string, win: AnalyticsWindow): void {
  win.dataLayer = win.dataLayer || []
  function gtag(..._args: unknown[]) {
    win.dataLayer!.push(arguments)
  }
  win.gtag = win.gtag || gtag
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`)
  win.gtag('js', new Date())
  const pageUrl = new URL(win.location.href)
  pageUrl.searchParams.delete('bliss_quote_id')
  win.gtag('config', id, { page_location: pageUrl.href })
}

function startMetaPixel(id: string, win: AnalyticsWindow): void {
  if (!win.fbq) {
    const queue: unknown[] = []
    const fbq = ((...args: unknown[]) => {
      if (fbq.callMethod) fbq.callMethod(...args)
      else queue.push(args)
    }) as NonNullable<AnalyticsWindow['fbq']>
    fbq.queue = queue
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    win.fbq = fbq
    win._fbq = fbq
  }
  injectScript('https://connect.facebook.net/en_US/fbevents.js')
  win.fbq!('init', id)
  win.fbq!('track', 'PageView')
}

/**
 * Safe to call on every mount — only the first call wires anything up.
 * Ids can be passed explicitly; otherwise they come from the Vite env.
 */
export function initBlissFunnelAnalytics(overrides?: {
  ga4Id?: string | null
  metaPixelId?: string | null
}): void {
  rememberMarketingTouch()
  if (initialized || typeof window === 'undefined') return
  initialized = true

  ga4Id = overrides && 'ga4Id' in overrides ? overrides.ga4Id || null : readEnvId('VITE_GA4_MEASUREMENT_ID')
  metaPixelId = overrides?.metaPixelId ?? readEnvId('VITE_META_PIXEL_ID')

  const win = window as AnalyticsWindow
  try {
    if (ga4Id) startGa4(ga4Id, win)
  } catch {
    /* analytics must never break the funnel */
  }
  try {
    if (metaPixelId) startMetaPixel(metaPixelId, win)
  } catch {
    /* analytics must never break the funnel */
  }
}

function pushBreadcrumb(event: string): void {
  try {
    const existing = JSON.parse(sessionStorage.getItem(BREADCRUMB_KEY) || '[]') as string[]
    existing.push(`${event}:${Date.now()}`)
    sessionStorage.setItem(BREADCRUMB_KEY, JSON.stringify(existing.slice(-MAX_BREADCRUMBS)))
  } catch {
    /* private browsing / storage disabled */
  }
}

/** Records one funnel event locally and forwards it to whatever is configured. */
export function trackBlissFunnelEvent(event: string, params?: FunnelEventParams): void {
  const name = normalizeFunnelEventName(event)
  if (!name) return

  pushBreadcrumb(name)

  if (typeof window === 'undefined') return
  const win = window as AnalyticsWindow

  try {
    // Works for GA4 via gtag and for a hand-installed GTM container.
    if (win.gtag) win.gtag('event', name, params || {})
    else if (win.dataLayer) win.dataLayer.push({ event: name, ...(params || {}) })
  } catch {
    /* ignore */
  }

  try {
    if (win.fbq) win.fbq('trackCustom', name, params || {})
  } catch {
    /* ignore */
  }
}

export function readBlissFunnelBreadcrumbs(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(BREADCRUMB_KEY) || '[]') as string[]
  } catch {
    return []
  }
}

/** Pipe-separated trail for the lead note, e.g. "gate_view:169… | gate_submit:169…". */
export function blissFunnelBreadcrumbSummary(): string {
  return readBlissFunnelBreadcrumbs().join(' | ')
}

/** Test-only reset so each case starts from a clean slate. */
export function resetBlissFunnelAnalyticsForTests(): void {
  initialized = false
  ga4Id = null
  metaPixelId = null
  generatedLeadIds.clear()
}




const generatedLeadIds = new Set<string>();
/** A successful saved quote, once per browser session; never send contact data or IDs to GA4. */
export function trackGa4GenerateLead(quoteRequestId: string, source: string): void {
  if (!quoteRequestId || typeof window === 'undefined') return;
  const win = window as AnalyticsWindow;
  if (!win.gtag && !win.dataLayer) return;
  const key = `bliss_ga4_lead_${quoteRequestId}`;
  try { if (sessionStorage.getItem(key)) return; } catch { /* optional storage */ }
  if (generatedLeadIds.has(quoteRequestId)) return;
  try {
    const params = { lead_source: source };
    if (win.gtag) win.gtag('event', 'generate_lead', params);
    else win.dataLayer!.push({ event: 'generate_lead', ...params });
    generatedLeadIds.add(quoteRequestId);
    try { sessionStorage.setItem(key, '1'); } catch { /* in-memory dedupe remains */ }
  } catch { /* analytics never blocks a saved inquiry */ }
}
